import { Injectable, inject } from '@angular/core';
import { Control, type EchartsConfig, type ILaneConfig, type LaneConfig, type StoreControl } from 'commons';
import type { LineSeriesOption, SeriesOption, XAXisComponentOption, YAXisComponentOption } from 'echarts';
import type { CommentModel } from '../../../services/api/models/CommentModel';
import type { KPI_Table } from '../../dtis-multilane-viewer/KPI_Table';
import { DtisCommentsEchartsMarkArea } from '../classes/commentMarkArea.class';
import { DtisSeries } from '../classes/series.class';
import { TestPhaseMarkArea } from '../classes/testPhaseMarkArea.class';
import { ConfidenceBandBuilderService } from './confidence-band-builder.service';
import { type AnomalyLaneIntervals, KpiSeriesBuilderService } from './kpi-series-builder.service';
import { SeriesDataService } from './series-data.service';

/**
 * Service responsible for building series configurations for ECharts multilane charts.
 * Coordinates with specialized services for data extraction, KPI series, and confidence bands.
 */
@Injectable({ providedIn: 'root' })
export class SeriesBuilderService {
  private readonly seriesDataService = inject(SeriesDataService);
  private readonly confidenceBandBuilder = inject(ConfidenceBandBuilderService);
  private readonly kpiSeriesBuilder = inject(KpiSeriesBuilderService);

  /**
   * Builds series data for all lanes
   */
  async buildSeries(
    lanes: LaneConfig[] | ILaneConfig[],
    xAxis: XAXisComponentOption[],
    yAxis: YAXisComponentOption[],
    comments: CommentModel[],
    highlightedComment?: string | null,
    config?: EchartsConfig
  ): Promise<DtisSeries[]> {
    if (!lanes.some((l) => l.controls.length > 0)) {
      return [new DtisSeries()];
    }

    const seriesPromises = lanes.flatMap((laneConfig, laneIndex) =>
      this.buildLaneSeries(laneConfig, laneIndex, xAxis, yAxis, comments, highlightedComment, config)
    );

    const resolvedSeries = await Promise.all(seriesPromises);
    return resolvedSeries.filter((s): s is DtisSeries => s !== undefined);
  }

  /**
   * Injects KPI series into the existing series array
   */
  injectKpiSeries(kpiTable: KPI_Table, lanes: LaneConfig[] | ILaneConfig[], yAxis: YAXisComponentOption[], existingSeries: unknown[]): AnomalyLaneIntervals[] {
    return this.kpiSeriesBuilder.injectKpiSeries(kpiTable, lanes, yAxis, existingSeries);
  }

  /**
   * Applies confidence band series to the chart
   */
  async applyConfidenceBands(lanes: LaneConfig[] | ILaneConfig[], existingSeries: unknown[]): Promise<void> {
    const laneIndices = lanes.map((_, idx) => idx);
    const controlIndices = lanes.flatMap((lane) => lane.controls.map((_, idx) => idx));
    const controls = lanes.flatMap((lane) => lane.controls);

    await this.confidenceBandBuilder.applyConfidenceBands(controls, laneIndices, controlIndices, existingSeries);
  }

  /**
   * Creates base series styles from config
   */
  createSeriesStylesFromConfig(config?: EchartsConfig): {
    [key: string]: LineSeriesOption;
  } {
    const baseLineStyle: LineSeriesOption = {
      type: 'line',
      symbol: config?.seriesSymbol ?? 'circle',
      symbolSize: config?.seriesSymbolSize ?? 2,
      smooth: config?.seriesSmooth ?? true,
      z: config?.seriesZLevel ?? 2,
      step: config?.seriesStep ?? false,
      sampling: config?.seriesSampling ?? 'none',
      connectNulls: config?.seriesConnectNulls ?? false,
      clip: config?.seriesClip ?? true,
      lineStyle: {
        join: 'round',
        opacity: config?.seriesLineOpacity ?? 1,
        width: config?.seriesLineWidth ?? 2,
        type: config?.seriesLineType ?? 'solid',
      },
      emphasis: {
        focus: config?.seriesEmphasisFocus ?? 'none',
        itemStyle: {},
        scale: config?.seriesEmphasisScale ?? true,
      },
      areaStyle: {
        opacity: config?.seriesAreaOpacity ?? 0,
      },
      showSymbol: config?.seriesShowSymbol ?? true,
    };

    return {
      line: { ...baseLineStyle },
      scatter: {
        ...baseLineStyle,
        type: 'scatter' as const,
        symbolSize: config?.seriesSymbolSize ?? 6,
      } as unknown as LineSeriesOption,
      pie: {
        type: 'pie',
      } as unknown as LineSeriesOption,
    };
  }

  private buildLaneSeries(
    laneConfig: LaneConfig | ILaneConfig,
    laneIndex: number,
    xAxis: XAXisComponentOption[],
    yAxis: YAXisComponentOption[],
    comments: CommentModel[],
    highlightedComment?: string | null,
    config?: EchartsConfig
  ): Promise<DtisSeries | undefined>[] {
    return laneConfig.controls
      .filter((control) => control && !control.isHidden)
      .filter((control) => !this.isTestStateChannel(control))
      .map((control, controlIndex, controls) =>
        this.createSeriesForControl({ control, controlIndex, controls }, { laneConfig, laneIndex, xAxis, yAxis }, { comments, highlightedComment, config })
      );
  }

  private async createSeriesForControl(
    controlData: {
      control: StoreControl;
      controlIndex: number;
      controls: StoreControl[];
    },
    laneContext: {
      laneConfig: LaneConfig | ILaneConfig;
      laneIndex: number;
      xAxis: XAXisComponentOption[];
      yAxis: YAXisComponentOption[];
    },
    annotationConfig: {
      comments: CommentModel[];
      highlightedComment?: string | null;
      config?: EchartsConfig;
    }
  ): Promise<DtisSeries | undefined> {
    const { control, controlIndex, controls } = controlData;
    const { laneConfig, laneIndex, xAxis, yAxis } = laneContext;
    const { comments, highlightedComment, config } = annotationConfig;

    const { id, label: name, user_label, color, seriesOverride } = control;
    const xAxisIndex = this.findXAxisIndex(xAxis, laneIndex);
    const isTimeAxis = this.isTimeAxis(xAxis, xAxisIndex);

    const seriesStylesMap = this.createSeriesStylesFromConfig(config);

    let data: unknown = [];
    try {
      data =
        isTimeAxis && !control.isOffline
          ? await this.seriesDataService.getTimeBasedSeriesData(control, laneConfig)
          : await this.seriesDataService.getIndexBasedSeriesData(new Control(control), laneConfig);
    } catch (error) {
      console.error('Error while fetching series data', error);
    }

    const chartType = (laneConfig.config?.chartType as string) ?? 'line';
    const commentsWithLocalVisibility = comments.map((comment) => ({
      ...comment,
      showInLane: comment.showInLane ?? true,
    }));

    const series = new DtisSeries(data, {
      ...seriesStylesMap[chartType],
      id: id + laneIndex + controlIndex,
      name: user_label || name,
      markArea: DtisCommentsEchartsMarkArea.convert(control, commentsWithLocalVisibility, highlightedComment),
      xAxisIndex,
      yAxisIndex: this.findYAxisIndex(laneConfig, controls, controlIndex, yAxis),
      ...seriesOverride,
    } as SeriesOption);

    if (color) {
      series.applyColor(color);
    }
    series.applyControl(control);
    series.setMarkline(controlIndex, this.getMarkLineData(laneConfig, laneIndex, controlIndex));

    return series;
  }

  private findXAxisIndex(xAxis: XAXisComponentOption[], laneIndex: number): number {
    const index = xAxis.findIndex(({ id }) => id === `LaneX-${laneIndex}`);
    return Math.max(index, 0);
  }

  private isTimeAxis(xAxis: XAXisComponentOption[], index: number): boolean {
    return xAxis[index]?.type === 'time';
  }

  private findYAxisIndex(laneConfig: LaneConfig | ILaneConfig, controls: StoreControl[], controlIndex: number, yAxis: YAXisComponentOption[]): number {
    // First, try to find by control-to-yAxis mapping
    const indexByControl = yAxis.findIndex(
      (axis) => laneConfig.config.yAxises?.find((y) => y.controls?.includes(controls[controlIndex].id))?.id === axis.id?.toString()
    );

    if (indexByControl >= 0) {
      return indexByControl;
    }

    // Fallback: find by lane ID in yAxis ID
    const indexByLane = yAxis.findIndex((axis) => axis.id?.toString().includes(laneConfig.id));

    return Math.max(indexByLane, 0);
  }

  private getMarkLineData(laneConfig: LaneConfig | ILaneConfig, laneIndex: number, controlIndex: number): unknown[] | undefined {
    return laneConfig.config.thresholds?.map(({ value: yAxis, color }, index) => ({
      name: `m${laneIndex}${controlIndex}-${index}`,
      yAxis,
      lineStyle: { color },
    }));
  }

  /**
   * Checks if a control represents a test_state channel.
   * Test state channels are handled separately as mark areas instead of series.
   */
  private isTestStateChannel(control: StoreControl): boolean {
    return TestPhaseMarkArea.isTestStateChannel(control.label);
  }
}
